import { useEffect, useMemo, useRef, useState } from 'react';
import { createBotApi } from '../adapters/createBotApi';
import { DashboardPage, type ReconnectMenuControls } from './DashboardPage';
import { AdminPage } from './AdminPage';
import { AuthGate } from './AuthGate';
import { LandingPage } from './LandingPage';
import { PrivacyPage } from './PrivacyPage';
import { TermsPage } from './TermsPage';
import { StatusPage } from './StatusPage';
import { BlogListPage } from './BlogListPage';
import { BlogPostPage } from './BlogPostPage';
import { PaymentSuccessPage, PaymentCancelPage } from './PaymentResultPage';
import { I18nProvider, languages, useI18n } from '../i18n';

export function App() {
  const path = location.pathname;
  if (path === '/') return <LandingPage />;
  // Homes por idioma (SEO: /es/, /ru/… prerenderizadas con hreflang; el idioma
  // lo resuelve detectLang por el prefijo de la ruta)
  if (/^\/(es|pt|fr|de|ru|ko|zh|ja|tr)\/?$/.test(path)) return <LandingPage />;
  if (path === '/privacidad' || path === '/privacy') return <PrivacyPage />;
  if (path === '/terminos' || path === '/terms') return <TermsPage />;
  if (path === '/status') return <StatusPage />;
  if (path === '/blog') return <BlogListPage />;
  if (path.startsWith('/blog/')) return <BlogPostPage slug={path.slice(6)} />;
  if (path === '/pago/exito') return <PaymentSuccessPage />;
  if (path === '/pago/cancelado') return <PaymentCancelPage />;
  return <AppShell />;
}

function AppShell() {
  const api = useMemo(() => createBotApi(), []);
  const initialView = location.pathname.includes('/admin') ? 'admin' : 'dashboard';
  const [view, setView] = useState<'dashboard' | 'admin'>(initialView);
  const [dashboardSlotId, setDashboardSlotId] = useState<string | null>(null);
  const [dashboardState, setDashboardState] = useState({ license: 'inactive', linked: false, licenseDetail: '', isStripe: false });
  const [reconnectMenuControls, setReconnectMenuControls] = useState<ReconnectMenuControls | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <I18nProvider>
      <AuthenticatedShell
        api={api}
        view={view}
        setView={setView}
        dashboardSlotId={dashboardSlotId}
        setDashboardSlotId={setDashboardSlotId}
        dashboardState={dashboardState}
        setDashboardState={setDashboardState}
        reconnectMenuControls={reconnectMenuControls}
        setReconnectMenuControls={setReconnectMenuControls}
        menuOpen={menuOpen}
        setMenuOpen={setMenuOpen}
      />
    </I18nProvider>
  );
}

function AuthenticatedShell({
  api,
  view,
  setView,
  dashboardSlotId,
  setDashboardSlotId,
  dashboardState,
  setDashboardState,
  reconnectMenuControls,
  setReconnectMenuControls,
  menuOpen,
  setMenuOpen
}: {
  api: ReturnType<typeof createBotApi>;
  view: 'dashboard' | 'admin';
  setView: (view: 'dashboard' | 'admin') => void;
  dashboardSlotId: string | null;
  setDashboardSlotId: (slotId: string | null) => void;
  dashboardState: { license: string; linked: boolean; licenseDetail: string; isStripe: boolean };
  setDashboardState: (state: { license: string; linked: boolean; licenseDetail: string; isStripe: boolean }) => void;
  reconnectMenuControls: ReconnectMenuControls | null;
  setReconnectMenuControls: (controls: ReconnectMenuControls | null) => void;
  menuOpen: boolean;
  setMenuOpen: (updater: boolean | ((open: boolean) => boolean)) => void;
}) {
  const { language, setLanguage, t } = useI18n();
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!menuOpen) return;
    function handlePointerDown(event: PointerEvent) {
      if (!menuRef.current?.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [menuOpen, setMenuOpen]);

  return (
    <AuthGate api={api}>
      {(user, signOut) => (
        <CheckoutAutoTrigger userId={user?.id} email={user?.email}>
        <div className="app-shell">
          <header className="topbar">
            <div className="topbar-left">
              <div className="wb-menu-wrap" ref={menuRef}>
                <button
                  className="wb-logo"
                  aria-label={t('app.openAccountMenu')}
                  aria-expanded={menuOpen}
                  onClick={() => setMenuOpen((open) => !open)}
                >
                  WB
                </button>
                {menuOpen && (
                  <div className="wb-menu">
                    <section>
                      <strong>{t('app.account')}</strong>
                      <span>{user?.email || t('app.localAdmin')}</span>
                      <button type="button" onClick={signOut}>
                        {t('app.signOut')}
                      </button>
                    </section>
                    <section>
                      <strong>{t('app.language')}</strong>
                      <select value={language} onChange={(event) => setLanguage(event.target.value as typeof language)}>
                        {languages.map((item) => (
                          <option key={item.code} value={item.code}>
                            {item.label}
                          </option>
                        ))}
                      </select>
                    </section>
                    <section>
                      <strong>{t('app.license')}</strong>
                      <span>{dashboardState.license}</span>
                      {dashboardState.licenseDetail && <span className="license-detail">{dashboardState.licenseDetail}</span>}
                      {dashboardState.isStripe && (
                        <button
                          type="button"
                          onClick={async () => {
                            const res = await fetch('/.netlify/functions/create-billing-portal', {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ user_id: user?.id }),
                            });
                            const data = await res.json();
                            if (data.url) window.location.href = data.url;
                          }}
                        >
                          {t('app.manageBilling')}
                        </button>
                      )}
                      {!dashboardState.isStripe && (
                        <div className="wb-plan-options">
                          <button
                            type="button"
                            className="wb-plan-btn"
                            onClick={async () => {
                              const res = await fetch('/.netlify/functions/create-checkout', {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ plan: 'pro_monthly', user_id: user?.id, email: user?.email }),
                              });
                              const data = await res.json();
                              if (data.url) window.location.href = data.url;
                            }}
                          >
                            Pro <span>€9,99/mes</span>
                          </button>
                          <button
                            type="button"
                            className="wb-plan-btn wb-plan-btn-gold"
                            onClick={async () => {
                              const res = await fetch('/.netlify/functions/create-checkout', {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ plan: 'pro_plus', user_id: user?.id, email: user?.email }),
                              });
                              const data = await res.json();
                              if (data.url) window.location.href = data.url;
                            }}
                          >
                            Pro+ <span>€19,99/mes</span>
                          </button>
                        </div>
                      )}
                    </section>
                    <section>
                      <strong>{t('app.configuration')}</strong>
                      {reconnectMenuControls ? (
                        <ReconnectMenuSection controls={reconnectMenuControls} />
                      ) : (
                        <span>{t('app.configurationUnavailable')}</span>
                      )}
                    </section>
                    <section>
                      <strong>{t('app.faq')}</strong>
                      <button type="button" onClick={() => { window.location.href = '/#faq'; }}>
                        {t('app.faqButton')}
                      </button>
                    </section>
                    {(user?.role === 'admin' || api.mode === 'mock') && (
                      <section>
                        <strong>{t('app.admin')}</strong>
                        <button type="button" onClick={() => setView(view === 'admin' ? 'dashboard' : 'admin')}>
                          {view === 'admin' ? t('app.dashboard') : t('app.adminPanel')}
                        </button>
                      </section>
                    )}
                  </div>
                )}
              </div>
              {view === 'dashboard' && (
                <span className="topbar-status" aria-label="Estado de licencia y vinculacion">
                  <StatusPill label={dashboardState.license} ok={['pro', 'pro+', 'active', 'trial'].includes(dashboardState.license)} />
                  <StatusPill label={dashboardState.linked ? 'linked' : 'unlinked'} ok={dashboardState.linked} />
                </span>
              )}
            </div>
            <nav className="topbar-right">
              {api.mode === 'supabase' && <button onClick={signOut}>{t('app.signOut')}</button>}
            </nav>
          </header>
          {view === 'admin' && (user?.role === 'admin' || api.mode === 'mock') ? (
            <AdminPage
              api={api}
              onOpenDashboardSlot={(slotId) => {
                setDashboardSlotId(slotId);
                setView('dashboard');
              }}
            />
          ) : (
            <DashboardPage
              api={api}
              preferredSlotId={dashboardSlotId}
              onSlotStateChange={setDashboardState}
              onReconnectMenuChange={setReconnectMenuControls}
            />
          )}
        </div>
        </CheckoutAutoTrigger>
      )}
    </AuthGate>
  );
}

function CheckoutAutoTrigger({
  userId,
  email,
  children,
}: {
  userId?: string;
  email?: string;
  children: React.ReactNode;
}) {
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const plan = params.get('checkout');
    if (!plan || !userId) return;
    // remove the param from URL without reload
    const url = new URL(location.href);
    url.searchParams.delete('checkout');
    history.replaceState(null, '', url.toString());

    fetch('/.netlify/functions/create-checkout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ plan, user_id: userId, email }),
    })
      .then((r) => r.json())
      .then((data) => { if (data.url) window.location.href = data.url; });
  }, [userId, email]);

  return <>{children}</>;
}

function StatusPill({ label, ok }: { label: string; ok: boolean }) {
  return <span className={`status-pill ${ok ? 'ok' : 'bad'}`}>{label}</span>;
}

function ReconnectMenuSection({ controls }: { controls: ReconnectMenuControls }) {
  const { t } = useI18n();
  const settings = controls.settings;
  return (
    <div className="wb-reconnect-config">
      <label className="check reconnect-toggle">
        <input
          type="checkbox"
          checked={settings.autoEnabled}
          disabled={!controls.canSave}
          onChange={(event) => controls.onSettingsChange({ ...settings, autoEnabled: event.target.checked })}
        />
        {t('dashboard.reconnectAuto')}
      </label>
      <label>
        {t('dashboard.reconnectDelay')}
        <div className="number-stepper compact-stepper">
          <button
            type="button"
            disabled={!controls.canSave}
            onClick={() => controls.onSettingsChange({ ...settings, delaySeconds: settings.delaySeconds - 30 })}
          >
            -
          </button>
          <input
            aria-label={t('dashboard.reconnectDelay')}
            type="number"
            min={30}
            max={3600}
            step={30}
            value={settings.delaySeconds}
            disabled={!controls.canSave}
            onChange={(event) => controls.onSettingsChange({ ...settings, delaySeconds: Number(event.target.value) })}
          />
          <button
            type="button"
            disabled={!controls.canSave}
            onClick={() => controls.onSettingsChange({ ...settings, delaySeconds: settings.delaySeconds + 30 })}
          >
            +
          </button>
        </div>
      </label>
      <button type="button" className="primary" disabled={!controls.canReconnect || controls.running} onClick={controls.onReconnectNow}>
        {t('dashboard.reconnectNow')}
      </button>
    </div>
  );
}
